import { useState } from 'react';

// ─── Data ────────────────────────────────────────────────────────────────────

const ROLES = [
  {
    id: 'admin',
    label: 'Admin',
    color: 'from-violet-500 to-purple-600',
    badgeColor: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    icon: '👑',
    description: 'Full system control — manages users, settings, and all data.',
  },
  {
    id: 'customer_care',
    label: 'Customer Care',
    color: 'from-cyan-500 to-blue-600',
    badgeColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    icon: '🎧',
    description: 'Front-line agent — creates leads, screens patients, manages follow-ups.',
  },
  {
    id: 'cc_head',
    label: 'CC Head',
    color: 'from-emerald-500 to-teal-600',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    icon: '📊',
    description: 'Team lead — oversees CC agents, reviews rejections, approves escalations.',
  },
];

const STAGES = [
  {
    id: 'new',
    label: 'Lead Created',
    stage: 'Stage 1',
    icon: '🆕',
    color: 'from-slate-500 to-slate-600',
    dot: 'bg-slate-400',
    roles: {
      admin: {
        can: ['Create lead from any source', 'Assign CC agent', 'Set priority', 'View all leads'],
        cannot: [],
        action: 'Create & Assign',
      },
      customer_care: {
        can: ['Create lead from call/walk-in', 'Fill patient details', 'Assign self or team member'],
        cannot: ['Cannot assign other CC agents leads', 'Cannot delete leads'],
        action: 'Create Lead',
      },
      cc_head: {
        can: ['View all new leads in team', 'Reassign leads between agents', 'Create leads if needed'],
        cannot: [],
        action: 'Oversee & Assign',
      },
    },
  },
  {
    id: 'screening',
    label: 'Screening',
    stage: 'Stage 2',
    icon: '📋',
    color: 'from-blue-500 to-blue-600',
    dot: 'bg-blue-400',
    roles: {
      admin: {
        can: ['View all screening data', 'Override screening decisions', 'Mark patient_ready manually'],
        cannot: [],
        action: 'View & Override',
      },
      customer_care: {
        can: [
          'Call the patient',
          'Fill screening form (eligibility, condition, willingness)',
          'Set timeline: Immediate / Later',
          'Schedule follow-up date',
          'Mark patient as patient_ready',
          'Mark as not_interested (close lead)',
        ],
        cannot: ['Cannot approve clinical decisions'],
        action: 'Screen Patient',
      },
      cc_head: {
        can: ['Monitor screening progress of all agents', 'View stale leads in screening', 'Reassign if agent is unavailable'],
        cannot: [],
        action: 'Monitor Progress',
      },
    },
  },
  {
    id: 'doc_verification',
    label: 'Document Upload',
    stage: 'Stage 3',
    icon: '📁',
    color: 'from-amber-500 to-orange-500',
    dot: 'bg-amber-400',
    roles: {
      admin: {
        can: ['Upload any document type', 'Delete/replace documents', 'View all uploaded files', 'Force move to next stage'],
        cannot: [],
        action: 'Manage Documents',
      },
      customer_care: {
        can: [
          'Upload medical reports (from patient)',
          'Upload Aadhaar, insurance cards',
          'Fill full medical history form',
          'Fill vitals (BP, sugar, weight, height)',
          'Fill allergy & medication history',
          'Send document request to patient via WhatsApp',
        ],
        cannot: ['Cannot delete documents once uploaded'],
        action: 'Upload & Fill',
      },
      cc_head: {
        can: ['View document completeness across all leads', 'Flag incomplete document cases', 'Nudge CC agents for missing docs'],
        cannot: [],
        action: 'Audit Documents',
      },
    },
  },
  {
    id: 'clinical_approval',
    label: 'Doctor Review',
    stage: 'Stage 4',
    icon: '🩺',
    color: 'from-indigo-500 to-indigo-600',
    dot: 'bg-indigo-400',
    roles: {
      admin: {
        can: ['View doctor review status', 'Re-assign to different doctor', 'Override rejection decision'],
        cannot: [],
        action: 'Oversee Review',
      },
      customer_care: {
        can: ['View if doctor has approved or rejected', 'Follow up with doctor if pending too long', 'Notify patient of approval'],
        cannot: ['Cannot approve or reject — only doctors can'],
        action: 'Track & Notify',
      },
      cc_head: {
        can: ['View all cases pending doctor review', 'Escalate stale reviews to admin', 'Handle doctor_rejected cases — reassign or close'],
        cannot: [],
        action: 'Escalate & Handle Rejections',
      },
    },
  },
  {
    id: 'hospital_assignment',
    label: 'Hospital Assignment',
    stage: 'Stage 5',
    icon: '🏥',
    color: 'from-rose-500 to-pink-600',
    dot: 'bg-rose-400',
    roles: {
      admin: {
        can: ['Assign any hospital', 'View all hospital capacity', 'Change hospital assignment', 'Coordinate with hospital directly'],
        cannot: [],
        action: 'Assign Hospital',
      },
      customer_care: {
        can: ['View which hospital is assigned', 'Communicate assignment info to patient'],
        cannot: ['Cannot assign or change hospital — Admin/CC Head only'],
        action: 'Inform Patient',
      },
      cc_head: {
        can: ['Assign hospital if admin is unavailable', 'Check bed availability', 'Coordinate with hospital team'],
        cannot: [],
        action: 'Assign & Coordinate',
      },
    },
  },
  {
    id: 'scheduling',
    label: 'Surgery Scheduling',
    stage: 'Stage 6',
    icon: '📅',
    color: 'from-teal-500 to-cyan-600',
    dot: 'bg-teal-400',
    roles: {
      admin: {
        can: ['Create surgery booking', 'Set date, time, OT room', 'Add medical team members', 'Set pre-op checklist', 'View all schedules'],
        cannot: [],
        action: 'Create & Manage Booking',
      },
      customer_care: {
        can: ['Confirm schedule with patient via call', 'Send schedule details via WhatsApp', 'Update patient on pre-op instructions'],
        cannot: ['Cannot create or modify the surgery booking record'],
        action: 'Confirm with Patient',
      },
      cc_head: {
        can: ['Review all scheduled surgeries', 'Ensure pre-op checklist is complete', 'Resolve scheduling conflicts'],
        cannot: [],
        action: 'Audit Schedules',
      },
    },
  },
  {
    id: 'payment_pending',
    label: 'Payment',
    stage: 'Stage 7',
    icon: '💳',
    color: 'from-yellow-500 to-amber-600',
    dot: 'bg-yellow-400',
    roles: {
      admin: {
        can: ['View all payment statuses', 'Generate payment links (via accountant)', 'Override payment status', 'Issue refunds'],
        cannot: [],
        action: 'Full Payment Oversight',
      },
      customer_care: {
        can: ['Check if patient has paid', 'Remind patient to pay (call/WhatsApp)', 'Inform patient of payment confirmation'],
        cannot: ['Cannot generate or manage payment links'],
        action: 'Follow-up on Payment',
      },
      cc_head: {
        can: ['View pending payment leads across team', 'Escalate stuck payments to accountant', 'Report payment delays to admin'],
        cannot: [],
        action: 'Monitor & Escalate',
      },
    },
  },
  {
    id: 'surgery',
    label: 'Surgery (OTP)',
    stage: 'Stage 8',
    icon: '⚕️',
    color: 'from-red-500 to-rose-600',
    dot: 'bg-red-400',
    roles: {
      admin: {
        can: [
          'View live OTP verification status',
          'Re-send OTP if not delivered',
          'View surgery start and completion timestamps',
          'Handle aborted surgery cases',
          'Set penalty and fault assignment',
        ],
        cannot: [],
        action: 'Monitor & Manage OTPs',
      },
      customer_care: {
        can: [
          'Track real-time surgery status via Socket.IO',
          'View if OTP 1 (Start) is verified',
          'View if OTP 2 (Completion) is verified',
          'Notify family if needed',
        ],
        cannot: ['Cannot verify OTPs — only surgeon/anesthesiologist can'],
        action: 'Track Surgery Status',
      },
      cc_head: {
        can: ['Monitor all active surgeries in real-time', 'Escalate OTP failures immediately', 'Handle abort/reschedule decisions'],
        cannot: [],
        action: 'Live Oversight',
      },
    },
  },
  {
    id: 'closure',
    label: 'Post-Op Monitoring',
    stage: 'Stage 9',
    icon: '🏥',
    color: 'from-purple-500 to-violet-600',
    dot: 'bg-purple-400',
    roles: {
      admin: {
        can: ['View post-op logs', 'Mark monitoring complete', 'Move lead to payout stage manually'],
        cannot: [],
        action: 'Oversee Post-Op',
      },
      customer_care: {
        can: [
          'Call patient for post-op check (Day 1, 2, 3)',
          'Add post-op monitoring log entries (visit date, notes)',
          'Mark monitoring as complete after 3 days',
          'Trigger payout notification to accountant',
        ],
        cannot: [],
        action: 'Monitor & Log',
      },
      cc_head: {
        can: ['Audit post-op completion across all cases', 'Ensure 3-day monitoring is done', 'Approve payout trigger if CC completed monitoring'],
        cannot: [],
        action: 'Audit Completion',
      },
    },
  },
  {
    id: 'payout_processing',
    label: 'Payout',
    stage: 'Stage 10',
    icon: '💰',
    color: 'from-green-500 to-emerald-600',
    dot: 'bg-green-400',
    roles: {
      admin: {
        can: ['View all payout statuses', 'Override payout amounts', 'View doctor/hospital/agent shares', 'Approve payout disbursements'],
        cannot: [],
        action: 'Full Payout Control',
      },
      customer_care: {
        can: ['Confirm payout notification was triggered after post-op', 'View payout status for their leads'],
        cannot: ['Cannot process or modify payouts — accountant only'],
        action: 'View Status',
      },
      cc_head: {
        can: ['Monitor payout completion for team leads', 'Escalate stuck payouts to admin', 'View commission breakdown for agents'],
        cannot: [],
        action: 'Monitor & Escalate',
      },
    },
  },
  {
    id: 'completed',
    label: 'Completed',
    stage: 'Stage 11',
    icon: '✅',
    color: 'from-emerald-500 to-green-600',
    dot: 'bg-emerald-400',
    roles: {
      admin: {
        can: ['View completed case archive', 'Generate case report PDF', 'View full audit trail (ActivityLog)', 'Export data'],
        cannot: [],
        action: 'Archive & Report',
      },
      customer_care: {
        can: ['View their completed leads', 'See conversion stats for personal performance'],
        cannot: ['Cannot modify completed leads'],
        action: 'View History',
      },
      cc_head: {
        can: ['View team performance reports', 'Track team conversion rates', 'Export performance CSV/PDF'],
        cannot: [],
        action: 'Team Performance',
      },
    },
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function RoleFlowDashboard() {
  const [activeRole, setActiveRole] = useState('admin');
  const [activeStage, setActiveStage] = useState(null);

  const role = ROLES.find((r) => r.id === activeRole);
  const selectedStage = STAGES.find((s) => s.id === activeStage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white font-sans p-4 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🏥</span>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
              Doxez CRM — Role Flow Dashboard
            </h1>
          </div>
          <p className="text-slate-400 text-sm md:text-base ml-12">
            Select a role to see what they can do at each stage of the patient journey.
          </p>
        </div>

        {/* Role Switcher */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {ROLES.map((r) => (
            <button
              key={r.id}
              onClick={() => { setActiveRole(r.id); setActiveStage(null); }}
              className={`relative p-5 rounded-2xl border text-left transition-all duration-300 group ${activeRole === r.id
                  ? `bg-gradient-to-br ${r.color} border-transparent shadow-2xl scale-[1.02]`
                  : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800'
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{r.icon}</span>
                <span className="text-lg font-bold">{r.label}</span>
                {activeRole === r.id && (
                  <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">Active</span>
                )}
              </div>
              <p className={`text-xs leading-relaxed ${activeRole === r.id ? 'text-white/80' : 'text-slate-400'}`}>
                {r.description}
              </p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left: Stage List */}
          <div className="xl:col-span-1">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-4">
              11 Stages — Click to Explore
            </h2>
            <div className="space-y-2">
              {STAGES.map((stage, idx) => {
                const stageData = stage.roles[activeRole];
                return (
                  <button
                    key={stage.id}
                    onClick={() => setActiveStage(activeStage === stage.id ? null : stage.id)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all duration-200 group ${activeStage === stage.id
                        ? `bg-gradient-to-r ${stage.color} border-transparent shadow-lg`
                        : 'bg-slate-800/40 border-slate-700/40 hover:border-slate-600 hover:bg-slate-800/60'
                      }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${activeStage === stage.id ? 'bg-white/20' : 'bg-slate-700'
                      }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{stage.icon}</span>
                        <span className={`text-sm font-medium truncate ${activeStage === stage.id ? 'text-white' : 'text-slate-200'}`}>
                          {stage.label}
                        </span>
                      </div>
                      <div className={`text-xs mt-0.5 ${activeStage === stage.id ? 'text-white/70' : 'text-slate-500'}`}>
                        {stageData.action}
                      </div>
                    </div>
                    <span className={`text-xs shrink-0 transition-transform duration-200 ${activeStage === stage.id ? 'rotate-90' : ''}`}>
                      ›
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Detail Panel */}
          <div className="xl:col-span-2">
            {!activeStage ? (
              /* Summary View */
              <div>
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-4">
                  {role.icon} {role.label} — Quick Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {STAGES.map((stage) => {
                    const stageData = stage.roles[activeRole];
                    return (
                      <button
                        key={stage.id}
                        onClick={() => setActiveStage(stage.id)}
                        className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-500 hover:bg-slate-800 text-left transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span>{stage.icon}</span>
                          <span className="text-sm font-semibold text-white">{stage.label}</span>
                          <span className="ml-auto text-[10px] text-slate-500">{stage.stage}</span>
                        </div>
                        <div className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${role.badgeColor}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          {stageData.action}
                        </div>
                        <p className="text-xs text-slate-500 mt-2 line-clamp-2">
                          {stageData.can[0]}
                          {stageData.can.length > 1 && ` +${stageData.can.length - 1} more`}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Detailed Stage View */
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
                    Stage Detail
                  </h2>
                  <button
                    onClick={() => setActiveStage(null)}
                    className="text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1"
                  >
                    ← Back to summary
                  </button>
                </div>

                {selectedStage && (() => {
                  const stageData = selectedStage.roles[activeRole];
                  return (
                    <div>
                      {/* Stage Header */}
                      <div className={`p-6 rounded-2xl bg-gradient-to-br ${selectedStage.color} mb-5 relative overflow-hidden`}>
                        <div className="absolute top-0 right-0 text-8xl opacity-10 leading-none pt-2 pr-2">
                          {selectedStage.icon}
                        </div>
                        <div className="relative">
                          <div className="text-xs font-semibold text-white/60 mb-1">{selectedStage.stage}</div>
                          <h3 className="text-2xl font-bold text-white mb-2">{selectedStage.label}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{role.icon}</span>
                            <span className="text-white/90 font-medium">{role.label}</span>
                            <span className="mx-2 text-white/30">·</span>
                            <span className="text-sm bg-white/20 px-3 py-1 rounded-full font-medium">
                              {stageData.action}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Can Do */}
                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 mb-4">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-green-400 text-lg">✅</span>
                          <h4 className="font-semibold text-white">What this role CAN do</h4>
                          <span className="ml-auto text-xs text-slate-500">{stageData.can.length} actions</span>
                        </div>
                        <div className="space-y-2.5">
                          {stageData.can.map((item, i) => (
                            <div key={i} className="flex items-start gap-3 group">
                              <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center shrink-0 mt-0.5">
                                <span className="text-green-400 text-xs">✓</span>
                              </div>
                              <p className="text-sm text-slate-300 leading-relaxed">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Cannot Do */}
                      {stageData.cannot.length > 0 && (
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 mb-4">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="text-red-400 text-lg">🚫</span>
                            <h4 className="font-semibold text-white">Restrictions for this role</h4>
                          </div>
                          <div className="space-y-2.5">
                            {stageData.cannot.map((item, i) => (
                              <div key={i} className="flex items-start gap-3">
                                <div className="w-5 h-5 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center shrink-0 mt-0.5">
                                  <span className="text-red-400 text-xs">✗</span>
                                </div>
                                <p className="text-sm text-slate-400 leading-relaxed">{item}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Compare All Roles at this Stage */}
                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
                        <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                          <span>🔄</span> Compare All Roles at This Stage
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {ROLES.map((r) => {
                            const rd = selectedStage.roles[r.id];
                            return (
                              <div
                                key={r.id}
                                className={`p-3.5 rounded-xl border transition-all ${r.id === activeRole
                                    ? `bg-gradient-to-br ${r.color} border-transparent`
                                    : 'bg-slate-900/60 border-slate-700/40'
                                  }`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <span>{r.icon}</span>
                                  <span className="text-sm font-semibold">{r.label}</span>
                                  {r.id === activeRole && (
                                    <span className="ml-auto text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">You</span>
                                  )}
                                </div>
                                <p className={`text-xs leading-relaxed ${r.id === activeRole ? 'text-white/80' : 'text-slate-500'}`}>
                                  {rd.can[0]}
                                </p>
                                {rd.can.length > 1 && (
                                  <p className={`text-[11px] mt-1 ${r.id === activeRole ? 'text-white/60' : 'text-slate-600'}`}>
                                    +{rd.can.length - 1} more actions
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-slate-800 flex items-center justify-between">
          <p className="text-xs text-slate-600">Doxez CRM — Healthcare Workflow System</p>
          <p className="text-xs text-slate-600">Hedena Healthcare · {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}